<script lang="ts">
  import { mdiCalendarBlankOutline, mdiCalendarMultiple } from "@mdi/js"
  import ToggleCTA from "@kontextwerk/web-sdk/ui/actions/ToggleCTA.svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import ActionBox from "@kontextwerk/web-sdk/ui/layout/ActionBox.svelte"
  import StatusTag from "@kontextwerk/web-sdk/ui/display/StatusTag.svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import { mdiCheckAll, mdiPlusCircleOutline } from "@mdi/js"
  import { availabilityFor } from "../../_logic/working-hours-view"
  import { buildFlexSlotFrames } from "../../_logic/flex-slots"
  import { addDays, setTime } from "../../_logic/date-math"
  import { toInputValue, toIsoString, type WorkingHoursByUser } from "../../_logic/form-state"
  import { ceilPositiveMinutesToStep, SCHEDULING_MINUTE_STEP } from "../../_logic/time-granularity"

  const DAY_START_HOUR = 6
  const DAY_END_HOUR = 22
  const MAX_SERIES_OCCURRENCES = 200
  const DEFAULT_SERIES_WEEKDAYS = [0, 1, 2, 3, 4]

  type PlanningKind = "single" | "series"
  type SeriesPattern = "DAILY" | "WEEKDAYS" | "WEEKLY"

  let {
    startValue = $bindable(""),
    endValue = $bindable(""),
    timeframeStartValue = $bindable(""),
    timeframeEndValue = $bindable(""),
    minStartInput = "",
    formLocked = false,
    autoEndEnabled = false,
    autoDurationMinutes = 60,
    startInPast = false,
    hasConflict = false,
    isExistingEvent = false,
    flexType = "HARD" as ScheduleEventFlexType,
    employeeId = "",
    workingHoursByUser = {},
    flexSlotMinutes = null,
    flexSlotsPerDay = null,
    planningKind = $bindable<PlanningKind>("single"),
    allowSeries = true,
    showSeriesConflictSkip = true,
    seriesStartTime = $bindable(""),
    seriesRangeStart = $bindable<Date | null>(null),
    seriesRangeEnd = $bindable<Date | null>(null),
    seriesPattern = $bindable<SeriesPattern>("WEEKLY"),
    seriesWeekdays = $bindable<number[]>([]),
    seriesIgnoreConflicts = $bindable(true),
    layout = "inline" as "inline" | "actionBox",
    actionBoxLabel = "Planung",
  } = $props()

  const computeDurationMinutes = (start: unknown, end: unknown) => {
    const startIso = toIsoString(start as any)
    const endIso = toIsoString(end as any)

    if (!startIso || !endIso) return null

    const startDate = new Date(startIso)
    const endDate = new Date(endIso)
    const diffMinutes = (endDate.getTime() - startDate.getTime()) / 60000

    if (!Number.isFinite(diffMinutes) || diffMinutes <= 0) return null

    return ceilPositiveMinutesToStep(diffMinutes)
  }

  const formatDateTime = (value: unknown) => {
    const iso = toIsoString(value as any)

    if (!iso) return "nicht hinterlegt"

    const date = new Date(iso)

    if (Number.isNaN(date.getTime())) return "nicht hinterlegt"

    return date.toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const formatDuration = (minutes: number | null | undefined) => {
    const parsed = Number(minutes ?? NaN)

    if (!Number.isFinite(parsed) || parsed <= 0) return "nicht hinterlegt"

    return `${ceilPositiveMinutesToStep(parsed, Math.round(parsed)) ?? Math.round(parsed)} Minuten`
  }

  const normalizeDurationMinutes = (value: unknown, fallback = 60) => {
    return ceilPositiveMinutesToStep(value, fallback) ?? fallback
  }

  const resolveInitialDurationMinutes = () =>
    computeDurationMinutes(startValue, endValue) ?? normalizeDurationMinutes(autoDurationMinutes)

  let durationMinutes = $state(resolveInitialDurationMinutes())
  let flexDay = $state<Date | null>(null)
  let flexSlotIndex = $state<number>(1)

  const weekdayLabels = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"] as const

  const flexDateMin = $derived(minStartInput ? minStartInput.slice(0, 10) : "")

  const seriesToggleDisabledReason = $derived.by(() => {
    if (formLocked) return "Eintrag ist schreibgeschützt."

    if (!allowSeries) return "Serien sind aktuell nur für Blocker/Abwesenheit verfügbar."

    if (isExistingEvent) return "Serien sind nur beim Anlegen möglich."

    if (flexType === "SOFT") return "Serien sind für Flextermine nicht verfügbar."

    return null
  })
  const canUseSeries = $derived(Boolean(!seriesToggleDisabledReason))
  const showSeriesToggle = $derived(!isExistingEvent && allowSeries && flexType !== "SOFT")
  const fieldSurfaceTone = "dark" as const

  const normalizeFlexMinutes = (value: number | null) => {
    return ceilPositiveMinutesToStep(value, 60) ?? 60
  }

  const effectiveDurationMinutes = $derived(
    flexType === "SOFT"
      ? Math.min(durationMinutes, normalizeFlexMinutes(flexSlotMinutes))
      : durationMinutes,
  )

  const parseDateOnly = (value: string | Date | null | undefined) => {
    if (!value) return null

    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) return null

      const copy = new Date(value)
      copy.setHours(0, 0, 0, 0)

      return copy
    }

    const datePart = value.slice(0, 10)

    if (!datePart) return null

    const parsed = new Date(`${datePart}T00:00:00`)

    if (Number.isNaN(parsed.getTime())) return null

    return parsed
  }
  const dateOnlyFromInput = (value: string | Date | null | undefined) => {
    const parsed = parseDateOnly(value)

    return parsed ? parsed.toISOString().slice(0, 10) : ""
  }
  const resolvedFlexDay = $derived.by(() =>
    parseDateOnly(flexDay ?? timeframeStartValue ?? startValue ?? minStartInput),
  )

  const buildFlexSlots = (day: Date, slotsPerDay: number | null) => {
    const availability = availabilityFor(
      workingHoursByUser as WorkingHoursByUser,
      employeeId || null,
      day,
    )
    const defaultStart = DAY_START_HOUR * 60
    const defaultEnd = DAY_END_HOUR * 60
    const startMinutes = availability?.startMinutes ?? defaultStart
    const endMinutes = availability?.endMinutes ?? defaultEnd
    const workWindows =
      availability?.windows?.length && !availability.dayOff
        ? availability.windows
        : [{ startMinutes, endMinutes }]
    const frames = buildFlexSlotFrames({
      workWindows,
      flexSlotsPerDay: slotsPerDay,
      stepMinutes: SCHEDULING_MINUTE_STEP,
    })
    const slots: Array<{ start: Date; end: Date; label: string }> = []
    for (const frame of frames) {
      const startMinute = frame.startMinutes
      const endMinute = frame.endMinutes
      const start = setTime(day, Math.floor(startMinute / 60), Math.round(startMinute % 60))
      const end = setTime(day, Math.floor(endMinute / 60), Math.round(endMinute % 60))
      const label = `${start.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })} – ${end.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}`
      slots.push({ start, end, label })
    }

    return slots
  }

  const updateEndFromStartAndDuration = (nextStartValue: string, minutes: number) => {
    if (flexType === "SOFT") return

    const startIso = toIsoString(nextStartValue as any)

    if (!startIso) return

    const start = new Date(startIso)

    if (Number.isNaN(start.getTime())) return

    const duration = normalizeDurationMinutes(minutes, durationMinutes)
    const nextEnd = new Date(start.getTime() + duration * 60000)
    const nextEndValue = toInputValue(nextEnd)

    if (nextEndValue && nextEndValue !== endValue) {
      endValue = nextEndValue
    }
  }

  const updateEndFromDuration = (minutes: number) => {
    updateEndFromStartAndDuration(startValue, minutes)
  }

  const handleStartChange = (event: Event) => {
    const currentDuration = durationMinutes
    const detailValue = (event as CustomEvent<{ value?: Date | string | null }>).detail?.value
    const target = event.target as HTMLInputElement | null
    const nextStartValue =
      detailValue instanceof Date
        ? (toInputValue(detailValue) ?? startValue)
        : typeof detailValue === "string"
          ? detailValue
          : (target?.value ?? startValue)
    startValue = nextStartValue
    updateEndFromStartAndDuration(nextStartValue, currentDuration)
  }

  const handleDurationChange = (event: Event) => {
    const target = event.target as HTMLInputElement | null
    const next = Number(target?.value ?? NaN)

    if (!Number.isFinite(next) || next <= 0) return

    const maxDuration = flexType === "SOFT" ? normalizeFlexMinutes(flexSlotMinutes) : null
    const requestedDuration = normalizeDurationMinutes(next)
    const nextDuration = maxDuration ? Math.min(requestedDuration, maxDuration) : requestedDuration

    if (target) target.value = String(nextDuration)

    durationMinutes = nextDuration
    updateEndFromDuration(nextDuration)
  }

  const pad2 = (value: number) => String(value).padStart(2, "0")

  const weekdayIndex = (date: Date) => (date.getDay() + 6) % 7 // Monday = 0

  const normalizeTimeInput = (raw: string) => {
    const trimmed = (raw ?? "").trim()

    if (!trimmed) return null

    const match = trimmed.match(/^(\d{1,2}):(\d{1,2})$/)

    if (!match) return null

    const hour = Number(match[1])
    const minute = Number(match[2])

    if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null

    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null

    const totalMinutes = Math.min(
      23 * 60 + 55,
      Math.ceil((hour * 60 + minute) / SCHEDULING_MINUTE_STEP) * SCHEDULING_MINUTE_STEP,
    )

    return `${pad2(Math.floor(totalMinutes / 60))}:${pad2(totalMinutes % 60)}`
  }

  const resolveTimeFromValue = (value: unknown) => {
    const iso = toIsoString(value as any)

    if (!iso) return ""

    const date = new Date(iso)

    if (Number.isNaN(date.getTime())) return ""

    return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`
  }

  const handleSeriesTimeChange = (event: Event) => {
    const target = event.target as HTMLInputElement | null
    const raw = target?.value ?? ""
    const normalized = normalizeTimeInput(raw)

    if (normalized) {
      seriesStartTime = normalized
      syncSeriesStartFromConfig()
    }
  }

  const toggleWeekday = (weekday: number) => {
    const base = Array.isArray(seriesWeekdays) ? seriesWeekdays : []
    const set = new Set(base)

    if (set.has(weekday)) {
      if (set.size <= 1) return

      set.delete(weekday)
    } else {
      set.add(weekday)
    }

    seriesWeekdays = Array.from(set).sort((a, b) => a - b)
  }

  const handleDayChange = (event: Event) => {
    const detailValue = (event as CustomEvent<{ value?: Date | null }>).detail?.value

    if (detailValue === null || detailValue instanceof Date) {
      flexDay = detailValue

      return
    }

    const target = event.target as HTMLInputElement | null
    const raw = target?.value ?? ""
    flexDay = raw ? parseDateOnly(raw) : null
  }

  const handleFlexSlotChange = (event: Event) => {
    const detail = (event as CustomEvent<{ value?: string; values?: string[] }>).detail
    const raw =
      (detail?.value as string | undefined) ??
      (Array.isArray(detail?.values) ? detail.values[0] : undefined) ??
      (event.target as HTMLInputElement | null)?.value
    const next = Number(raw ?? NaN)

    if (!Number.isFinite(next) || next <= 0) return

    flexSlotIndex = Math.round(next)
  }

  const addMonthsClamped = (date: Date, months: number) => {
    const base = new Date(date)

    if (Number.isNaN(base.getTime())) return base

    const year = base.getFullYear()
    const month = base.getMonth()
    const day = base.getDate()
    const targetMonthIndex = month + months
    const targetYear = year + Math.floor(targetMonthIndex / 12)
    const targetMonth = ((targetMonthIndex % 12) + 12) % 12
    const lastDay = new Date(targetYear, targetMonth + 1, 0).getDate()
    const clampedDay = Math.min(day, lastDay)
    const next = new Date(base)
    next.setFullYear(targetYear, targetMonth, clampedDay)

    return next
  }

  $effect(() => {
    if (planningKind !== "series") return

    if (!canUseSeries) {
      planningKind = "single"
    }
  })

  $effect(() => {
    if (planningKind !== "series" || !canUseSeries) return

    if (seriesPattern !== "WEEKLY") {
      seriesPattern = "WEEKLY"
    }

    if (!Array.isArray(seriesWeekdays) || seriesWeekdays.length === 0) {
      seriesWeekdays = [...DEFAULT_SERIES_WEEKDAYS]
    }
  })

  const applySeriesDefaults = () => {
    const selectedStartDay = parseDateOnly(startValue || minStartInput)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const baseDay = seriesRangeStart ?? selectedStartDay ?? today
    const needsInit =
      !seriesRangeStart &&
      !seriesRangeEnd &&
      !seriesStartTime &&
      (!Array.isArray(seriesWeekdays) || seriesWeekdays.length === 0)

    if (!seriesRangeStart && baseDay) {
      seriesRangeStart = baseDay
    }

    if (!seriesRangeEnd && baseDay) {
      seriesRangeEnd = addMonthsClamped(baseDay, 3)
    }

    if (!seriesStartTime) {
      seriesStartTime = resolveTimeFromValue(startValue || minStartInput) || "09:00"
    }

    if (seriesPattern !== "WEEKLY") {
      seriesPattern = "WEEKLY"
    }

    if (needsInit || !Array.isArray(seriesWeekdays) || seriesWeekdays.length === 0) {
      seriesWeekdays = [...DEFAULT_SERIES_WEEKDAYS]
    }
  }

  const syncSeriesStartFromConfig = () => {
    if (flexType === "SOFT") return

    if (planningKind !== "series") return

    if (!canUseSeries) return

    if (!seriesRangeStart) return

    const normalizedTime = normalizeTimeInput(seriesStartTime)

    if (!normalizedTime) return

    const [hourRaw, minuteRaw] = normalizedTime.split(":")
    const hours = Number(hourRaw)
    const minutes = Number(minuteRaw)

    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return

    const nextStart = toInputValue(setTime(seriesRangeStart, hours, minutes))
    const currentStart = toInputValue(startValue as any)

    if (nextStart && nextStart !== currentStart) {
      startValue = nextStart
      updateEndFromStartAndDuration(nextStart, durationMinutes)
    }
  }

  const clampSeriesRange = () => {
    if (planningKind !== "series") return

    if (!seriesRangeStart || !seriesRangeEnd) return

    const start = new Date(seriesRangeStart)
    start.setHours(0, 0, 0, 0)
    const end = new Date(seriesRangeEnd)
    end.setHours(0, 0, 0, 0)

    if (end.getTime() < start.getTime()) {
      seriesRangeEnd = start
    }
  }

  const setPlanningKind = (next: PlanningKind) => {
    if (next === "series" && canUseSeries) {
      planningKind = "series"
      applySeriesDefaults()
      clampSeriesRange()
      syncSeriesStartFromConfig()

      return
    }

    planningKind = "single"
  }

  const parseDateInputFromEvent = (event: Event) => {
    const detailValue = (event as CustomEvent<{ value?: Date | null }>).detail?.value

    if (detailValue === null || detailValue instanceof Date) return detailValue

    const target = event.target as HTMLInputElement | null
    const raw = target?.value ?? ""

    return raw ? parseDateOnly(raw) : null
  }

  const handleSeriesRangeStartChange = (event: Event) => {
    seriesRangeStart = parseDateInputFromEvent(event)
    clampSeriesRange()
    syncSeriesStartFromConfig()
  }

  const handleSeriesRangeEndChange = (event: Event) => {
    seriesRangeEnd = parseDateInputFromEvent(event)
    clampSeriesRange()
  }

  const flexSlotOptions = $derived.by(() => {
    if (flexType !== "SOFT") return []

    const dayDate = resolvedFlexDay

    if (!dayDate) return []

    const slots = buildFlexSlots(dayDate, flexSlotsPerDay)

    return slots.map((slot, index) => ({
      name: `Slot ${index + 1} (${slot.label})`,
      value: String(index + 1),
      start: slot.start,
      end: slot.end,
    }))
  })
  const clampedFlexSlotIndex = $derived.by(() => {
    const requested = Math.max(1, flexSlotIndex || 1)

    if (flexType !== "SOFT" || !flexSlotOptions.length) return requested

    return Math.min(flexSlotOptions.length, requested)
  })

  // Keep start/end + timeframe in sync with flex selections
  $effect(() => {
    if (flexType !== "SOFT") return

    const dayDate = resolvedFlexDay

    if (!dayDate) return

    const slots = buildFlexSlots(dayDate, flexSlotsPerDay)

    if (!slots.length) return

    const slot = slots[clampedFlexSlotIndex - 1]
    const duration = Number.isFinite(effectiveDurationMinutes)
      ? Math.max(1, effectiveDurationMinutes)
      : normalizeFlexMinutes(flexSlotMinutes)
    const endCandidate = new Date(slot.start.getTime() + duration * 60000)
    const clampedEnd = endCandidate.getTime() > slot.end.getTime() ? slot.end : endCandidate
    const nextStart = toInputValue(slot.start)
    const nextEnd = toInputValue(clampedEnd)
    const nextTimeframeStart = toInputValue(slot.start)
    const nextTimeframeEnd = toInputValue(slot.end)

    if (nextStart && nextStart !== startValue) startValue = nextStart

    if (nextEnd && nextEnd !== endValue) endValue = nextEnd

    if (nextTimeframeStart && nextTimeframeStart !== timeframeStartValue)
      timeframeStartValue = nextTimeframeStart

    if (nextTimeframeEnd && nextTimeframeEnd !== timeframeEndValue)
      timeframeEndValue = nextTimeframeEnd
  })

  const seriesPreview = $derived.by(() => {
    if (planningKind !== "series" || flexType === "SOFT") return null

    if (!seriesRangeStart || !seriesRangeEnd) return null

    const start = new Date(seriesRangeStart)
    start.setHours(0, 0, 0, 0)
    const end = new Date(seriesRangeEnd)
    end.setHours(0, 0, 0, 0)

    if (end.getTime() < start.getTime()) {
      return { count: 0, error: "Ende liegt vor dem Start." }
    }

    const timeOk = Boolean(normalizeTimeInput(seriesStartTime))

    if (!timeOk) {
      return { count: 0, error: "Bitte eine gültige Uhrzeit im Format HH:MM eingeben." }
    }

    const selectedWeekdays = Array.isArray(seriesWeekdays) ? seriesWeekdays : []

    if (!selectedWeekdays.length) {
      return { count: 0, error: "Bitte mindestens einen Wochentag auswählen." }
    }

    let count = 0
    const cursor = new Date(start)
    while (cursor.getTime() <= end.getTime()) {
      const wd = weekdayIndex(cursor)
      const include = selectedWeekdays.includes(wd)

      if (include) {
        count += 1

        if (count > MAX_SERIES_OCCURRENCES) {
          return {
            count,
            error: `Zu viele Einträge (${count}+). Bitte Zeitraum eingrenzen (max. ${MAX_SERIES_OCCURRENCES}).`,
          }
        }
      }

      cursor.setDate(cursor.getDate() + 1)
    }

    return { count, error: null }
  })
  const planningFacts = $derived.by(() => {
    if (flexType === "SOFT") {
      return [
        {
          label: "Tag",
          value: formatDateTime(resolvedFlexDay ?? startValue).split(",")[0] ?? "nicht hinterlegt",
        },
        {
          label: "Zeitfenster",
          value: `${formatDateTime(timeframeStartValue || startValue)} – ${formatDateTime(timeframeEndValue || endValue)}`,
        },
        { label: "Dauer", value: formatDuration(effectiveDurationMinutes) },
        flexSlotOptions.length
          ? {
              label: "Flexslot",
              value: flexSlotOptions[clampedFlexSlotIndex - 1]?.name ?? "nicht hinterlegt",
            }
          : null,
      ].filter(Boolean) as Array<{ label: string; value: string }>
    }

    return [
      { label: "Start", value: formatDateTime(startValue) },
      { label: "Ende", value: formatDateTime(endValue) },
      { label: "Dauer", value: formatDuration(effectiveDurationMinutes) },
    ]
  })
</script>

{#snippet seriesToggle(darkMode: boolean)}
  <ToggleCTA
    {darkMode}
    value={planningKind}
    allowReselect={false}
    options={[
      { value: "single", label: "Einzel", icon: mdiCalendarBlankOutline },
      {
        value: "series",
        label: "Serie",
        icon: mdiCalendarMultiple,
        disabled: !canUseSeries,
        tooltipContent: seriesToggleDisabledReason ?? null,
      },
    ]}
    onChange={(value) => {
      const next = value ?? "single"
      setPlanningKind((next === "series" ? "series" : "single") satisfies PlanningKind)
    }}
  />
{/snippet}

{#snippet inlineSeriesToggle()}
  {@render seriesToggle(true)}
{/snippet}

{#snippet actionBoxSeriesToggle()}
  {@render seriesToggle(true)}
{/snippet}

{#snippet editableFields()}
  {#if flexType === "SOFT"}
    <section class="row">
      <Input
        type="number"
        placeholder="Dauer (Minuten)"
        value={effectiveDurationMinutes}
        min={SCHEDULING_MINUTE_STEP}
        step={SCHEDULING_MINUTE_STEP}
        surfaceTone={fieldSurfaceTone}
        helperText={`Dauer für diesen Flexslot (Standard ${autoDurationMinutes} Min.)`}
        onChange={handleDurationChange}
        disabled={formLocked}
      />
      <Input
        type="date"
        placeholder="Tag"
        value={dateOnlyFromInput(resolvedFlexDay)}
        min={flexDateMin}
        surfaceTone={fieldSurfaceTone}
        onChange={handleDayChange}
        disabled={formLocked}
      />
    </section>
    <section class="row">
      <Input
        type="select"
        options={flexSlotOptions}
        placeholder="Flexslot"
        value={String(clampedFlexSlotIndex)}
        surfaceTone={fieldSurfaceTone}
        onChange={handleFlexSlotChange}
        disabled={formLocked || flexSlotOptions.length === 0}
        helperText={flexSlotOptions.length === 0
          ? "Keine Flexslots für diesen Tag verfügbar."
          : "Wähle den gewünschten Flexslot."}
      />
    </section>
  {:else if planningKind === "series" && canUseSeries}
    <section class="row">
      <Input
        type="time"
        placeholder="Start (Uhrzeit)"
        bind:value={seriesStartTime}
        step={SCHEDULING_MINUTE_STEP * 60}
        surfaceTone={fieldSurfaceTone}
        onChange={handleSeriesTimeChange}
        disabled={formLocked}
      />
      <Input
        type="number"
        placeholder="Dauer (Minuten)"
        value={effectiveDurationMinutes}
        min={SCHEDULING_MINUTE_STEP}
        step={SCHEDULING_MINUTE_STEP}
        surfaceTone={fieldSurfaceTone}
        onChange={handleDurationChange}
        disabled={formLocked}
      />
    </section>
    <section class="row">
      <Input
        type="date"
        placeholder="Von"
        value={dateOnlyFromInput(seriesRangeStart)}
        surfaceTone={fieldSurfaceTone}
        disabled={formLocked}
        onChange={handleSeriesRangeStartChange}
      />
      <Input
        type="date"
        placeholder="Bis"
        value={dateOnlyFromInput(seriesRangeEnd)}
        surfaceTone={fieldSurfaceTone}
        disabled={formLocked}
        onChange={handleSeriesRangeEndChange}
      />
    </section>
    <div class="weekday-field">
      <div class="weekday-label">Tage</div>
      <div class="weekday-toggles">
        {#each weekdayLabels as label, idx (label)}
          {@const active = seriesWeekdays?.includes(idx)}
          <button
            type="button"
            class="selectable-status-tag-button"
            aria-pressed={active}
            disabled={formLocked}
            onclick={() => toggleWeekday(idx)}
          >
            <StatusTag
              variant="status"
              size="sm"
              {label}
              color={active ? "emerald" : "#64748b"}
              icon={active ? mdiCheckAll : mdiPlusCircleOutline}
              disableTitle={true}
            />
          </button>
        {/each}
      </div>
    </div>
    {#if seriesPreview}
      {#if seriesPreview.error}
        <div class="inline-error">{seriesPreview.error}</div>
      {:else}
        <div class="series-preview">Erzeugt voraussichtlich {seriesPreview.count} Einträge.</div>
      {/if}
    {/if}
    {#if showSeriesConflictSkip}
      <section class="row">
        <Input
          type="checkbox"
          placeholder="Kollisionen automatisch ignorieren"
          bind:value={seriesIgnoreConflicts}
          tooltip="Falls ein Eintrag kollidiert, wird dieser Tag übersprungen statt die ganze Serie abzubrechen."
          disabled={formLocked}
        />
      </section>
    {/if}
  {:else}
    <section class="row">
      <Input
        type="datetime-local"
        placeholder="Start"
        bind:value={startValue}
        minuteIncrement={SCHEDULING_MINUTE_STEP}
        surfaceTone={fieldSurfaceTone}
        min={minStartInput}
        onChange={handleStartChange}
        disabled={formLocked}
      />
      <Input
        type="number"
        placeholder="Dauer (Minuten)"
        value={effectiveDurationMinutes}
        min={SCHEDULING_MINUTE_STEP}
        step={SCHEDULING_MINUTE_STEP}
        surfaceTone={fieldSurfaceTone}
        helperText={autoEndEnabled
          ? `Dauer wird automatisch vorgeschlagen (${autoDurationMinutes} Min. aus Auftrag).`
          : undefined}
        onChange={handleDurationChange}
        disabled={formLocked}
      />
    </section>
  {/if}
{/snippet}

{#if formLocked}
  <div class="light-action-box">
    <ActionBox label="Planung" darkMode={false}>
      <dl class="planning-summary">
        {#each planningFacts as fact}
          <div>
            <dt>{fact.label}</dt>
            <dd>{fact.value}</dd>
          </div>
        {/each}
      </dl>
    </ActionBox>
  </div>
{:else if layout === "actionBox"}
  <ActionBox
    label={actionBoxLabel}
    darkMode={false}
    buttonSnippet={showSeriesToggle ? actionBoxSeriesToggle : null}
  >
    <div class="schedule-time-fields-box">
      {@render editableFields()}
    </div>
  </ActionBox>
{:else}
  <SectionTitle
    small={true}
    title="Planung"
    styleOuter="margin-top: 0.3rem; margin-bottom: 0.2rem;"
  >
    {#snippet buttonSnippet()}
      {#if showSeriesToggle}
        {@render inlineSeriesToggle()}
      {/if}
    {/snippet}
  </SectionTitle>
  {@render editableFields()}
{/if}
{#if startInPast && !formLocked}
  <div class="inline-warning">
    Hinweis: Die Startzeit liegt in der Vergangenheit. Kehrblatt prüft das beim Anwenden.
  </div>
{/if}
{#if hasConflict}
  <div class="inline-warning">
    Hinweis: Dieser Zeitraum überschneidet sich mit einem bestehenden Termin dieses Mitarbeiters.
  </div>
{/if}

<style lang="less">
  @import "@kontextwerk/web-sdk/theme/tokens.less";

  .light-action-box {
    --overlay-text-color: var(--text-100);
  }

  .schedule-time-fields-box {
    --overlay-text-color: var(--text-100);
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
    padding-top: 0.15rem;
    color: var(--overlay-text-color);
  }

  .planning-summary {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 0.5rem 1rem;
    margin: 0;
  }

  .planning-summary div {
    min-width: 0;
  }

  .planning-summary dt {
    margin: 0;
    color: color-mix(in srgb, var(--overlay-text-color, var(--text-100)) 72%, transparent);
    font-size: 0.72rem;
    font-weight: 800;
    text-transform: uppercase;
  }

  .planning-summary dd {
    margin: 0.1rem 0 0;
    color: var(--overlay-text-color, var(--text-100));
    line-height: 1.3;
    overflow-wrap: anywhere;
  }

  .weekday-toggles {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
  }

  .selectable-status-tag-button {
    display: inline-flex;
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    cursor: pointer;
  }

  .selectable-status-tag-button:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  .weekday-field {
    margin-top: 0.15rem;
    margin-bottom: 0.25rem;
  }

  .weekday-label {
    color: var(--overlay-text-color, var(--text-100));
    font-size: 0.72rem;
    font-weight: 800;
    margin-bottom: 0.35rem;
    text-transform: uppercase;
  }

  .series-preview {
    font-size: 0.85rem;
    color: color-mix(in srgb, var(--overlay-text-color, var(--text-200)) 76%, transparent);
    margin-top: 0.25rem;
  }

  .inline-warning {
    font-size: 0.85rem;
    color: color-mix(in srgb, #b45309 78%, var(--overlay-text-color, var(--text-100)));
    margin-top: 0.25rem;
    line-height: 1.35;
  }
</style>
